/**
 * @file useStatementXeroCheck.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-19
 * @modified 2026-10-19
 *
 * The statement Xero-check on Generate Statements, kept out of that screen so
 * its own code stays about the workflow:
 *
 *  - "Sync with Xero" (steps 1 and 2): POST /api/statements/sync, followed
 *    with useJobStream; the last result from GET /api/statements/sync/latest.
 *  - The run's check (step 2): every statement's latest verdict comes with
 *    the run's list (applyList), the job is followed while it runs, and the
 *    list is read again when its verdicts are in.
 *  - The gates: "approve anyway?" for a statement that does not match
 *    (INTEGRITY_MISMATCH → acknowledgeMismatch), "go ahead without the
 *    check?" while it runs (CHECK_IN_PROGRESS → proceedWithoutCheck), and the
 *    words for a bulk approval that left mismatches out (skippedMismatch).
 *  - The integrity window (StatementIntegrityModal).
 *
 * Ordering is the client's only say: mismatches first (sortStatements). Every
 * verdict is the server's.
 *
 *   const xc = useStatementXeroCheck({ runId, dates, selectedContacts, user, reload, approve });
 *   ...loadStatements → xc.applyList(data)
 *   {xc.dialogs}
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from '../config';
import useJobStream from './useJobStream';
import usePersistedState from './usePersistedState';
import ConfirmDialog from '../components/ConfirmDialog';
import StatementIntegrityModal from '../components/StatementIntegrityModal';
import { showAlert } from '../components/Alert';
import { canWrite, xeroRequirement } from '../utils/permissions';
import {
  isLiveJob,
  isUnacknowledgedMismatch,
  sortStatements,
  effectiveIntegrityStatus
} from '../utils/statementIntegrity';

const EMPTY_META = {
  loaded: false, integrityAvailable: false, integrityReason: null, summary: null,
  checkJob: null, coverage: null, latestSync: null
};

/** Words for a bulk approval's answer: how many, and which mismatches it left alone. */
export function bulkApprovalText(data, scopeText = '') {
  const skipped = Array.isArray(data?.skippedMismatch) ? data.skippedMismatch : [];
  const base = `Updated ${data?.count ?? 0} statements${scopeText}.`;
  if (!skipped.length) return { type: 'success', message: base };
  const names = skipped.map((s) => s.accountNo || s.account || s.id).join(', ');
  return {
    type: 'warning',
    title: 'Statements that don’t match Xero were not approved',
    message: `${base}\n\n${skipped.length} statement${skipped.length === 1 ? '' : 's'} that ${skipped.length === 1 ? 'doesn’t' : 'don’t'} match Xero `
      + `${skipped.length === 1 ? 'was' : 'were'} left out: ${names}. Open each one's Xero check, and approve it on its own if it is right.`
  };
}

export default function useStatementXeroCheck({ runId, dates, selectedContacts, user, reload, approve }) {
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const approveRef = useRef(approve);
  approveRef.current = approve;
  const doReload = useCallback(() => (reloadRef.current ? reloadRef.current() : null), []);

  const canGenerate = canWrite(user, 'statements.generate');
  const xeroBlocked = xeroRequirement(user) === 'blocked';

  const [meta, setMeta] = useState(EMPTY_META);
  const [syncJobId, setSyncJobId] = usePersistedState('generateStatements.syncJobId', null);
  const [syncAttached, setSyncAttached] = useState(false);
  const [syncStarting, setSyncStarting] = useState(false);
  const [syncLast, setSyncLast] = useState(null);
  const [checkJobId, setCheckJobId] = usePersistedState('generateStatements.checkJobId', null);
  const [integrityModalId, setIntegrityModalId] = usePersistedState('generateStatements.integrityModalId', null);
  const [startingCheck, setStartingCheck] = useState(false);
  const [gateConfirm, setGateConfirm] = useState(null);         // { onContinue, etaSeconds }
  const [mismatchConfirm, setMismatchConfirm] = useState(null); // { statement, headline, reasons }
  const [rows, setRows] = useState([]);
  const announcedRef = useRef(new Set());

  /** The run's list answered: keep what it says about the check. */
  const applyList = useCallback((data) => {
    if (!data) return;
    setRows(Array.isArray(data.statements) ? data.statements : []);
    setMeta({
      loaded: true,
      integrityAvailable: data.integrityAvailable === true,
      integrityReason: data.integrityReason || null,
      summary: data.integritySummary || null,
      checkJob: data.checkJob || null,
      coverage: data.coverage || null,
      latestSync: data.latestSync || null
    });
    if (data.checkJob?.jobId) setCheckJobId((prev) => (prev === data.checkJob.jobId ? prev : data.checkJob.jobId));
  }, [setCheckJobId]);

  const reset = useCallback(() => {
    setMeta(EMPTY_META);
    setRows([]);
    setCheckJobId(null);
    setIntegrityModalId(null);
  }, [setCheckJobId, setIntegrityModalId]);

  // Another run opened on this screen (a hand-over, Your statement runs, a
  // new generation): what was followed belonged to the old one.
  const runRef = useRef(runId);
  useEffect(() => {
    if (runRef.current === runId) return;
    const had = runRef.current;
    runRef.current = runId;
    if (had !== null && had !== undefined) {
      setMeta(EMPTY_META);
      setCheckJobId(null);
      setIntegrityModalId(null);
    }
  }, [runId, setCheckJobId, setIntegrityModalId]);

  // ── Sync ──
  const loadSyncLast = useCallback(async () => {
    if (!dates?.end) return;
    try {
      const res = await fetch(`${API_URL}/api/statements/sync/latest?periodEnd=${encodeURIComponent(dates.end)}`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setSyncLast(data);
      if (data?.running?.jobId && !syncJobId) {
        setSyncJobId(data.running.jobId);
        setSyncAttached(true);
      }
    } catch (_) { /* the last result only */ }
  }, [dates?.end, syncJobId, setSyncJobId]);

  useEffect(() => { loadSyncLast(); }, [loadSyncLast]);

  const syncStream = useJobStream(syncJobId, {
    onComplete: (result) => {
      loadSyncLast();
      if (result?.runId) doReload();
    },
    onError: () => { loadSyncLast(); }
  });
  const syncLive = syncStarting || isLiveJob(syncStream.status);

  const syncDisabledReason = !selectedContacts?.length ? 'Choose the contacts first'
    : (!dates?.start || !dates?.end) ? 'Choose the statement period first'
      : !canGenerate ? 'You need permission to generate statements'
        : xeroBlocked ? 'Your account does not use Xero'
          : '';

  const startSync = useCallback(async () => {
    if (!selectedContacts?.length || !dates?.start || !dates?.end) return;
    setSyncStarting(true);
    try {
      const res = await fetch(`${API_URL}/api/statements/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          contactIds: selectedContacts.map((c) => c.id),
          periodStart: dates.start,
          periodEnd: dates.end,
          ...(runId ? { runId } : {})
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.code !== 'READ_ONLY') showAlert(`Could not start the sync: ${data.error || `HTTP ${res.status}`}`, 'error');
        return;
      }
      setSyncAttached(!!data.attached);
      setSyncJobId(data.jobId || null);
      if (data.message) showAlert(data.message, 'info');
    } catch (e) {
      showAlert(`Could not start the sync: ${e.message}`, 'error');
    } finally {
      setSyncStarting(false);
    }
  }, [selectedContacts, dates?.start, dates?.end, runId, setSyncJobId]);

  // A finished sync of another period is not this screen's.
  const syncResultForPeriod = syncStream.result && syncStream.result.periodEnd === dates?.end ? syncStream.result : null;
  const syncJob = {
    status: syncStarting ? 'starting' : syncStream.status,
    progress: syncStream.progress,
    result: syncResultForPeriod,
    error: syncStream.error,
    attached: syncAttached
  };

  // ── The run's check ──
  const checkStream = useJobStream(checkJobId, {
    onVerdictsDone: async () => {
      const data = await doReload();
      const jobId = checkJobId;
      if (jobId && !announcedRef.current.has(jobId) && Array.isArray(data)) {
        announcedRef.current.add(jobId);
        const n = data.filter(isUnacknowledgedMismatch).length;
        if (n > 0) showAlert(`Xero check: ${n} statement${n === 1 ? '' : 's'} ${n === 1 ? 'doesn’t' : 'don’t'} match — moved to the top in red`, 'warning');
      }
    },
    onComplete: () => { doReload(); },
    onError: () => { doReload(); }
  });
  const checkLive = isLiveJob(checkStream.status);
  const verdictsDone = !!checkStream.progress?.verdictsDone || checkStream.status === 'completed';
  // /list knows of a check this tab may not stream (a colleague's): follow it
  // by reading the list until its verdicts are in.
  const listPending = !!meta.checkJob && meta.checkJob.verdictsDone !== true;
  const followingList = !!meta.checkJob && meta.checkJob.jobId && meta.checkJob.jobId === checkJobId && (checkLive || checkStream.status === 'completed');
  const verdictGate = (checkLive && !verdictsDone) || (listPending && !followingList);
  const runCheckActive = checkLive || (listPending && !followingList);
  const eta = checkStream.progress?.etaSeconds || null;
  const gateTitle = verdictGate ? `Waiting for the Xero check${eta ? ` (about ${eta} s)` : ''}` : '';

  useEffect(() => {
    if (!runId || !listPending || followingList) return undefined;
    const t = setInterval(() => { doReload(); }, 5000);
    return () => clearInterval(t);
  }, [runId, listPending, followingList, doReload]);

  const recheckDisabledReason = !meta.integrityAvailable ? (meta.integrityReason || 'Not ready — migration statement_reconciliation.sql has not run')
    : !canGenerate ? 'You need permission to generate statements'
      : runCheckActive ? 'A Xero check of this run is already running'
        : startingCheck ? 'Starting…' : '';

  const startRunCheck = useCallback(async () => {
    if (!runId) return;
    setStartingCheck(true);
    try {
      const res = await fetch(`${API_URL}/api/statements/runs/${runId}/integrity-check`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({})
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.code !== 'READ_ONLY') showAlert(`Could not start the Xero check: ${data.error || `HTTP ${res.status}`}`, 'error');
        return;
      }
      if (data.available === false) { showAlert(data.reason || 'The Xero check is not ready yet', 'warning'); return; }
      if (data.jobId) setCheckJobId(data.jobId);
      doReload();
    } catch (e) {
      showAlert(`Could not start the Xero check: ${e.message}`, 'error');
    } finally {
      setStartingCheck(false);
    }
  }, [runId, setCheckJobId, doReload]);

  /** Generation answered: follow the check it started. */
  const followCheck = useCallback((integrityCheck) => {
    if (integrityCheck?.jobId) setCheckJobId(integrityCheck.jobId);
  }, [setCheckJobId]);

  // ── Gates ──
  /**
   * A 409 from the server about the check: open the right question and say
   * so (true), or leave the caller to handle it (false).
   * `retry({ proceedWithoutCheck: true })` / `retry({ acknowledgeMismatch: true })`.
   */
  const handleGate = useCallback((status, data, retry, statement = null) => {
    if (status !== 409 || !data) return false;
    if (data.code === 'CHECK_IN_PROGRESS') {
      if (data.jobId) setCheckJobId((prev) => (prev === data.jobId ? prev : data.jobId));
      setGateConfirm({ etaSeconds: data.etaSeconds || null, onContinue: () => retry({ proceedWithoutCheck: true }) });
      return true;
    }
    if (data.code === 'INTEGRITY_MISMATCH') {
      setMismatchConfirm({
        statement: statement || { id: null },
        headline: data.headline || statement?.integrity?.headline || '',
        reasons: Array.isArray(data.reasons) ? data.reasons : [],
        onContinue: () => retry({ acknowledgeMismatch: true })
      });
      doReload();
      return true;
    }
    return false;
  }, [setCheckJobId, doReload]);

  /** Before approving one statement: a mismatch asks first (true = asked; the caller stops). */
  const askBeforeApprove = useCallback((statement) => {
    if (!statement || !isUnacknowledgedMismatch(statement)) return false;
    setMismatchConfirm({
      statement,
      headline: statement.integrity?.headline || '',
      reasons: null,
      onContinue: () => approveRef.current && approveRef.current(statement.id, 'approved', { acknowledgeMismatch: true })
    });
    return true;
  }, []);

  // The reasons, when the question was asked from the row (not the server).
  useEffect(() => {
    const st = mismatchConfirm?.statement;
    if (!mismatchConfirm || mismatchConfirm.reasons || !st?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/statements/${st.id}/integrity`, { credentials: 'include' });
        const data = res.ok ? await res.json() : null;
        const reasons = (data?.latest?.reasons || []).filter((r) => r.severity === 'mismatch');
        if (!cancelled) setMismatchConfirm((m) => (m && m.statement === st ? { ...m, reasons, headline: data?.latest?.detail?.headline || m.headline } : m));
      } catch (_) {
        if (!cancelled) setMismatchConfirm((m) => (m && m.statement === st ? { ...m, reasons: [] } : m));
      }
    })();
    return () => { cancelled = true; };
  }, [mismatchConfirm]);

  const rowClass = useCallback((statement) => {
    const s = effectiveIntegrityStatus(statement?.integrity);
    if (s === 'mismatch') return statement.integrity?.acknowledged ? 'stmt-row-mismatch acknowledged' : 'stmt-row-mismatch';
    return '';
  }, []);

  const integrityRow = integrityModalId ? rows.find((s) => Number(s.id) === Number(integrityModalId)) || null : null;

  const dialogs = (
    <>
      <ConfirmDialog
        isOpen={!!gateConfirm}
        title="The Xero check is still running"
        type="warning"
        message={`The statements are still being checked against Xero${gateConfirm?.etaSeconds ? ` (about ${gateConfirm.etaSeconds} s left)` : ''}. Wait for it, or go ahead without it — going ahead is recorded in Activity.`}
        confirmText="Go ahead without it"
        cancelText="Wait"
        onConfirm={() => { const fn = gateConfirm?.onContinue; setGateConfirm(null); if (fn) fn(); }}
        onCancel={() => setGateConfirm(null)}
      />
      <ConfirmDialog
        isOpen={!!mismatchConfirm}
        title="This statement doesn’t match Xero — approve it anyway?"
        type="warning"
        message={mismatchConfirm ? (
          <div className="stmt-mismatch-confirm">
            <p>
              <strong>{mismatchConfirm.statement?.accountNo || mismatchConfirm.statement?.account || 'This statement'}</strong>
              {mismatchConfirm.headline ? ` — ${mismatchConfirm.headline}` : ''}
            </p>
            {mismatchConfirm.reasons === null ? <p>Loading the reasons…</p> : mismatchConfirm.reasons.length > 0 && (
              <ul>{mismatchConfirm.reasons.slice(0, 8).map((r, i) => <li key={`${r.code}-${i}`}>{r.message || r.code}</li>)}</ul>
            )}
            <p>Approving it anyway is recorded, with these reasons. If the check later finds something different, it will ask again.</p>
          </div>
        ) : ''}
        confirmText="Approve anyway"
        cancelText="Cancel"
        onConfirm={() => { const fn = mismatchConfirm?.onContinue; setMismatchConfirm(null); if (fn) fn(); }}
        onCancel={() => setMismatchConfirm(null)}
      />
      {integrityModalId && (
        <StatementIntegrityModal
          statementId={integrityModalId}
          row={integrityRow}
          periodEnd={dates?.end}
          canCheck={canGenerate}
          integrityAvailable={meta.integrityAvailable}
          runCheckActive={runCheckActive}
          onClose={() => { setIntegrityModalId(null); doReload(); }}
          onChanged={() => { doReload(); }}
        />
      )}
    </>
  );

  return {
    meta,
    applyList,
    reset,
    sortRows: sortStatements,
    rowClass,
    openIntegrity: (statement) => setIntegrityModalId(statement?.id || null),
    sync: { job: syncJob, last: syncLast?.last || null, start: startSync, live: syncLive, disabledReason: syncDisabledReason },
    check: {
      jobId: checkJobId, live: checkLive, progress: checkStream.progress, verdictGate, gateTitle,
      runCheckActive, start: startRunCheck, follow: followCheck, disabledReason: recheckDisabledReason
    },
    handleGate,
    askBeforeApprove,
    dialogs
  };
}
